import { useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query'
import { useEffect } from 'react'
import type { UnsubscribeFunc } from 'pocketbase'
import { pb } from './pb'
import type {
  ClubValue,
  CollectionMap,
  CollectionName,
  Competition,
  GalleryItem,
  Match,
  NavLink,
  NewsArticle,
  Page,
  Player,
  SiteSettings,
  Sponsor,
  StaffMember,
  Stat,
  TimelineEntry,
} from './types'

// ---------------------------------------------------------------------------
// Query keys. One factory so a realtime event can invalidate an entire
// collection without knowing which screens happen to be mounted.
// ---------------------------------------------------------------------------
export const keys = {
  all: (collection: CollectionName) => [collection] as const,
  list: (collection: CollectionName, params?: unknown) =>
    [collection, 'list', params ?? null] as const,
  one: (collection: CollectionName, id: string) => [collection, 'one', id] as const,
  bySlug: (collection: CollectionName, slug: string) => [collection, 'slug', slug] as const,
}

interface ListParams {
  filter?: string
  sort?: string
  expand?: string
  perPage?: number
}

/** Fetches every published row of a collection (these are all small tables). */
function useFullList<K extends CollectionName>(
  collection: K,
  params: ListParams = {},
  options?: Partial<UseQueryOptions<CollectionMap[K][]>>
) {
  return useQuery<CollectionMap[K][]>({
    queryKey: keys.list(collection, params),
    queryFn: async () =>
      (await pb.collection(collection).getFullList({
        batch: params.perPage ?? 200,
        ...params,
      })) as unknown as CollectionMap[K][],
    ...options,
  })
}

// ---------------------------------------------------------------------------
// Realtime. PocketBase pushes record changes over SSE; every event simply
// invalidates that collection's queries and React Query refetches what is
// actually on screen. One subscription set covers the whole app.
// ---------------------------------------------------------------------------
const LIVE_COLLECTIONS: CollectionName[] = [
  'site_settings',
  'nav_links',
  'stats',
  'players',
  'staff',
  'competitions',
  'matches',
  'news',
  'pages',
  'timeline',
  'values',
  'sponsors',
  'gallery',
]

export function useRealtimeSync() {
  const queryClient = useQueryClient()

  useEffect(() => {
    let cancelled = false
    const unsubscribers: UnsubscribeFunc[] = []

    for (const collection of LIVE_COLLECTIONS) {
      void pb
        .collection(collection)
        .subscribe('*', () => {
          void queryClient.invalidateQueries({ queryKey: keys.all(collection) })
        })
        .then((unsubscribe) => {
          // The component may have unmounted while the handshake was in flight.
          if (cancelled) void unsubscribe()
          else unsubscribers.push(unsubscribe)
        })
        .catch(() => {
          // A dropped realtime connection degrades to normal polling-on-focus
          // rather than breaking the page.
        })
    }

    return () => {
      cancelled = true
      for (const unsubscribe of unsubscribers) void unsubscribe()
    }
  }, [queryClient])
}

// ---------------------------------------------------------------------------
// Public-site queries
// ---------------------------------------------------------------------------

/** The singleton settings row. Everything global on the site reads from this. */
export function useSettings() {
  return useQuery<SiteSettings | null>({
    queryKey: keys.list('site_settings'),
    queryFn: async () => {
      const rows = await pb.collection('site_settings').getList(1, 1)
      return (rows.items[0] as unknown as SiteSettings) ?? null
    },
    // Settings change rarely and gate the whole layout — keep them warm.
    staleTime: 60_000,
  })
}

export const useNavLinks = () =>
  useFullList<'nav_links'>('nav_links', { filter: 'published = true', sort: 'sort' })

export const useStats = () =>
  useFullList<'stats'>('stats', { filter: 'published = true', sort: 'sort' })

export const usePlayers = () =>
  useFullList<'players'>('players', {
    filter: 'published = true',
    sort: 'sort,squad_number',
  })

export const useStaff = () =>
  useFullList<'staff'>('staff', { filter: 'published = true', sort: 'sort' })

export const useTimeline = () =>
  useFullList<'timeline'>('timeline', { filter: 'published = true', sort: 'sort' })

export const useValues = () =>
  useFullList<'values'>('values', { filter: 'published = true', sort: 'sort' })

export const useSponsors = () =>
  useFullList<'sponsors'>('sponsors', { filter: 'published = true', sort: 'sort' })

export const useGallery = () =>
  useFullList<'gallery'>('gallery', { filter: 'published = true', sort: 'sort,-created' })

export const useCompetitions = () =>
  useFullList<'competitions'>('competitions', { filter: 'published = true', sort: 'sort' })

export const useMatches = () =>
  useFullList<'matches'>('matches', {
    filter: 'published = true',
    sort: '-kickoff',
    expand: 'competition',
  })

export function usePlayer(slug: string | undefined) {
  return useQuery<Player | null>({
    queryKey: keys.bySlug('players', slug ?? ''),
    enabled: Boolean(slug),
    queryFn: async () =>
      (await pb
        .collection('players')
        .getFirstListItem(`slug = ${JSON.stringify(slug)}`)) as unknown as Player,
  })
}

export function useNewsList(category?: string) {
  const filter = ['published = true', category ? `category = ${JSON.stringify(category)}` : '']
    .filter(Boolean)
    .join(' && ')

  return useFullList<'news'>('news', { filter, sort: '-published_at' })
}

export function useArticle(slug: string | undefined) {
  return useQuery<NewsArticle | null>({
    queryKey: keys.bySlug('news', slug ?? ''),
    enabled: Boolean(slug),
    queryFn: async () =>
      (await pb.collection('news').getFirstListItem(`slug = ${JSON.stringify(slug)}`, {
        expand: 'related_match',
      })) as unknown as NewsArticle,
  })
}

export function useMatch(id: string | undefined) {
  return useQuery<Match | null>({
    queryKey: keys.one('matches', id ?? ''),
    enabled: Boolean(id),
    queryFn: async () =>
      (await pb
        .collection('matches')
        .getOne(id as string, { expand: 'competition' })) as unknown as Match,
  })
}

/** Long-form editable page, looked up by its route-bound slug. */
export function usePage(slug: string) {
  return useQuery<Page | null>({
    queryKey: keys.bySlug('pages', slug),
    queryFn: async () =>
      (await pb
        .collection('pages')
        .getFirstListItem(`slug = ${JSON.stringify(slug)}`)) as unknown as Page,
    // A missing page is a legitimate state (the club deleted it), not an error
    // worth retrying four times.
    retry: false,
  })
}

// Re-exported so screens can import the record types from one place.
export type {
  ClubValue,
  Competition,
  GalleryItem,
  Match,
  NavLink,
  NewsArticle,
  Page,
  Player,
  SiteSettings,
  Sponsor,
  StaffMember,
  Stat,
  TimelineEntry,
}
